import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { useEffect, useState } from 'react';
import { Grid, Tooltip } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import AutocompleteComponent from '../../../../components/layout/Autocomplete';
import useTranslate from '../../../../hooks/useTranslate';
import { useRoles } from '../../../../hooks/useRoles';
import { useAppDispatch } from '../../../../hooks/reduxHooks';
import { setSelectedOrganizationId } from '../../../../redux/slices/organizationsSlice';
import { useOrganizationFilter } from '../../../../contexts/OrganizationFilterContext';
import useOrganizationSelector from '../../../../components/layout/OrganizationSelector/hook/useOrganizationSelector';
import { useGetOrganismsQuery } from '../../../../services/organismsApi';
import { TEST_IDS } from '../../consts/testIds';
import { SRT_STATES, SRT_STATES_OPTIONS } from '../../consts/states';
import { ALLOWED_VISIT_TYPE_IDS_SET } from '../consts/visitTypes';
import {
    EXCLUDED_ORGANIZATION_IDS,
    EXCLUDED_CONTRACT_IDS,
    EXCLUDED_ORGANISM_IDS
} from '../../consts/excludedIds';
import { isAutoseguro } from '../../../../constants/organizationLegends';

const EXCLUDED_CUITS = ['30500052089', '30707519092'];

interface VisitsFiltersProps {
    extraFilters: any;
    handleChangeExtraFilters: (e: any) => void;
    contractsOptions: any;
    isFetchingContractsOptions: any;
    establishmentsOptions: any;
    isFetchingEstablishmentsOptions: any;
    setFilters: any;
    handleDateChange: any;
    visitTypesOptions: { id: number; nombre: string; value: number }[];
    isFetchingVisitTypes: boolean;
    onOrganizationChange?: () => void;
    isShowMoreFiltersOpen?: boolean;
}

const VisitsFilters = (props: VisitsFiltersProps) => {
    const {
        extraFilters,
        contractsOptions,
        isFetchingContractsOptions,
        establishmentsOptions,
        isFetchingEstablishmentsOptions,
        handleChangeExtraFilters,
        visitTypesOptions,
        isFetchingVisitTypes,
        onOrganizationChange,
        isShowMoreFiltersOpen
    } = props;

    const hasDateRange = Boolean(extraFilters?.fechaInicio || extraFilters?.fechaFin);
    const hasVisitDate = Boolean(extraFilters?.fecha);

    const toDateKey = (value: any) => (value && dayjs(value).isValid() ? dayjs(value).toISOString() : null);

    const [draftFechaInicio, setDraftFechaInicio] = useState<any>(extraFilters?.fechaInicio ?? null);
    const [draftFechaFin, setDraftFechaFin] = useState<any>(extraFilters?.fechaFin ?? null);

    // Si el rango se limpia/cambia desde afuera (Limpiar filtros, cambio de
    // pestaña) sincronizamos el borrador para no dejar valores stale.
    useEffect(() => {
        setDraftFechaInicio(extraFilters?.fechaInicio ?? null);
        setDraftFechaFin(extraFilters?.fechaFin ?? null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extraFilters?.fechaInicio, extraFilters?.fechaFin]);

    // Fecha desde/hasta solo se aplican cuando el rango está completo: evita
    // disparar una búsqueda con un solo extremo cargado.
    useEffect(() => {
        const startKey = toDateKey(draftFechaInicio);
        const endKey = toDateKey(draftFechaFin);
        const committedStartKey = toDateKey(extraFilters?.fechaInicio);
        const committedEndKey = toDateKey(extraFilters?.fechaFin);

        if (startKey === committedStartKey && endKey === committedEndKey) return;

        if (startKey && endKey) {
            handleChangeExtraFilters({ target: { name: 'fecha', value: null } });
            handleChangeExtraFilters({ target: { name: 'fechaInicio', value: draftFechaInicio } });
            handleChangeExtraFilters({ target: { name: 'fechaFin', value: draftFechaFin } });
        } else if (committedStartKey || committedEndKey) {
            handleChangeExtraFilters({ target: { name: 'fechaInicio', value: null } });
            handleChangeExtraFilters({ target: { name: 'fechaFin', value: null } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftFechaInicio, draftFechaFin]);

    const { t } = useTranslation('filesGenerationSRT');
    const { translate } = useTranslate(t);

    const dispatch = useAppDispatch();

    const { isPerfilBackOffice } = useRoles();
    const { selectedOrganization, setSelectedOrganization } = useOrganizationFilter();
    const { organizations: organizationsOptions } = useOrganizationSelector();

    const {
        data: { data: { data: organisms } } = { data: { data: [] } },
        isFetching: isFetchingOrganisms
    } = useGetOrganismsQuery({
        idOrganizacion: extraFilters?.idOrganizacion || undefined,
        skip: 0,
        take: 999
    });

    const [allOrganisms, setAllOrganisms] = useState<any[]>([]);
    const [filteredOrganisms, setFilteredOrganisms] = useState<any[]>([]);

    useEffect(() => {
        if (organisms && organisms?.length > 0) {
            const formattedOrganisms = organisms
                .filter(
                    (organismo: any) =>
                        !EXCLUDED_CUITS.includes(organismo.cuit) &&
                        !EXCLUDED_ORGANISM_IDS.has(organismo.id)
                )
                .map((organismo: any) => ({
                    label: `${organismo.cuit} - ${organismo.nombre}`,
                    value: organismo.id,
                    idOrganismoCentral: organismo?.idOrganismoCentral
                }));
            setAllOrganisms(formattedOrganisms);
        }
    }, [organisms]);

    const selectedContractId = extraFilters?.idContrato?.id ?? extraFilters?.idContrato ?? null;

    useEffect(() => {
        if (selectedContractId) {
            const filtered = allOrganisms.filter(
                (organismo) => organismo.idOrganismoCentral === selectedContractId
            );
            setFilteredOrganisms(filtered);

            handleChangeExtraFilters({ target: { name: 'idOrganismo', value: null } });
        } else {
            setFilteredOrganisms(allOrganisms);
        }
    }, [selectedContractId, allOrganisms]);

    // Sincronizar el valor del contexto con el filtro local
    useEffect(() => {        
        if (selectedOrganization && extraFilters?.idOrganizacion !== selectedOrganization) {
            handleChangeExtraFilters({
                target: {
                    name: 'idOrganizacion',
                    value: selectedOrganization
                }
            });
            dispatch(setSelectedOrganizationId(selectedOrganization));
        }
    }, [selectedOrganization, extraFilters?.idOrganizacion, handleChangeExtraFilters]);

    const handleOrganizationChange = (value: any) => {
        const organizationId = value?.id ?? null;

        setSelectedOrganization(organizationId);

        handleChangeExtraFilters({
            target: {
                name: 'idOrganizacion',
                value: organizationId
            }
        });
        handleChangeExtraFilters({ target: { name: 'idContrato', value: null } });
        handleChangeExtraFilters({ target: { name: 'idOrganismo', value: null } });
        handleChangeExtraFilters({ target: { name: 'idEstablecimiento', value: null } });

        onOrganizationChange?.();
    };
    
    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Grid container spacing={2}>
                    {isPerfilBackOffice && (
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <AutocompleteComponent
                                value={
                                    organizationsOptions.find(
                                        (org: any) => org.id === selectedOrganization
                                    ) || null
                                }
                                options={(organizationsOptions || []).filter(
                                    (org: any) => !isAutoseguro(org.nombre)
                                )}
                                size="small"
                                name="idOrganizacion"
                                loading={false}
                                label={t('header.extraFilters.organization')}
                                templateLabel={(option: any) => {
                                    return `${option?.nombre}`;
                                }}
                                onChange={handleOrganizationChange}
                                clearable
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={4} lg={isPerfilBackOffice ? 3 : 6}>
                        <AutocompleteComponent
                            label={`${translate('header.extraFilters.contract')}`}
                            size="small"
                            name="idContrato"
                            disabled={isFetchingContractsOptions}
                            loading={isFetchingContractsOptions}
                            onChange={(value: any) => {
                                handleChangeExtraFilters({
                                    target: {
                                        name: 'idContrato',
                                        value: value
                                    }
                                });
                                handleChangeExtraFilters({ target: { name: 'idEstablecimiento', value: null } });
                            }}
                            templateLabel={(item: any) =>
                                `${item?.cuit || ''} ${item?.cuit && '-'} ${item?.nombre || ''}`
                            }
                            options={(contractsOptions || []).filter(
                                (c: any) => !EXCLUDED_CONTRACT_IDS.has(c.id)
                            )}
                            value={extraFilters?.idContrato || ''}
                            testID={TEST_IDS.extraFilters.visits.contract}
                            clearable
                        />
                    </Grid>
                    {isPerfilBackOffice && (
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <AutocompleteComponent
                                label={`${translate('header.extraFilters.organism')}`}
                                size="small"
                                name="organismo"
                                disabled={isFetchingOrganisms}
                                loading={isFetchingOrganisms}
                                onChange={(value: any) => {
                                    handleChangeExtraFilters({
                                        target: { name: 'idOrganismo', value: value?.value || '' }
                                    });
                                    handleChangeExtraFilters({ target: { name: 'idEstablecimiento', value: null } });
                                }}
                                templateLabel={(option: any) => option?.label || ''}
                                options={filteredOrganisms}
                                value={
                                    filteredOrganisms.find(
                                        (option) => option.value === extraFilters?.idOrganismo
                                    ) || null
                                }
                                testID={TEST_IDS.extraFilters.visits.organism}
                                clearable
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={4} lg={isPerfilBackOffice ? 3 : 6}>
                        <AutocompleteComponent
                            label={`${translate('header.extraFilters.establishment')}`}
                            size="small"
                            name="idEstablecimiento"
                            disabled={isFetchingEstablishmentsOptions}
                            loading={isFetchingEstablishmentsOptions}
                            onChange={(value: any) => {
                                handleChangeExtraFilters({
                                    target: {
                                        name: 'idEstablecimiento',
                                        value: value
                                    }
                                });
                            }}
                            templateLabel={(item: any) =>
                                `${item?.numero || ''} ${item?.numero && '-'} ${item?.nombre || ''}`
                            }
                            options={(establishmentsOptions || []).filter(
                                (est: any) =>
                                    !EXCLUDED_ORGANIZATION_IDS.has(est.organizacion?.id) &&
                                    !EXCLUDED_CONTRACT_IDS.has(est.organismo?.organismoCentral?.id) &&
                                    !EXCLUDED_ORGANISM_IDS.has(est.organismo?.id)
                            )}
                            value={extraFilters?.idEstablecimiento || ''}
                            testID={TEST_IDS.extraFilters.visits.establishment}
                            clearable
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4} lg={4}>
                        <AutocompleteComponent
                            label={`${translate('header.extraFilters.visitType')}`}
                            size="small"
                            name="idTipo"
                            disabled={isFetchingVisitTypes}
                            loading={isFetchingVisitTypes}
                            onChange={(value: any) => {
                                handleChangeExtraFilters({
                                    target: {
                                        name: 'idTipo',
                                        value: value
                                    }
                                });
                            }}
                            options={visitTypesOptions.filter((o) => ALLOWED_VISIT_TYPE_IDS_SET.has(o.id))}
                            value={extraFilters?.idTipo || null}
                            testID={TEST_IDS.extraFilters.visits.visitType}
                            clearable
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4}>
                        <AutocompleteComponent
                            label={`${translate('header.extraFilters.stateSRT')}`}
                            size="small"
                            name="idEstadoSRT"
                            disabled={false}
                            loading={false}
                            onChange={(value: any) => {
                                handleChangeExtraFilters({
                                    target: {
                                        name: 'idEstadoSRT',
                                        value: value
                                    }
                                });
                            }}
                            options={SRT_STATES_OPTIONS.filter(o => o.id === SRT_STATES.PENDING || o.id === SRT_STATES.GENERATED)}
                            templateLabel={(srtState: { id: SRT_STATES; nombre: string }) => {
                                return translate(`srtStates.${srtState?.nombre}`);
                            }}
                            value={extraFilters?.idEstadoSRT || ''}
                            clearable
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4}>
                        <Tooltip title={hasDateRange ? translate('header.extraFilters.onlyOneDateFilterTooltip') : ''}>
                            <span>
                                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                                    <DatePicker
                                        slotProps={{
                                            textField: {
                                                inputProps: { 'data-testid': 'visits-filter-visit-date' }
                                            },
                                            field: { clearable: true }
                                        }}
                                        sx={{display: 'flex'}}
                                        label="Fecha de Visita"
                                        value={extraFilters?.fecha}
                                        name="fecha"
                                        disabled={hasDateRange}
                                        onChange={(value: any) => {
                                            if (value) {
                                                handleChangeExtraFilters({ target: { name: 'fechaInicio', value: null } });
                                                handleChangeExtraFilters({ target: { name: 'fechaFin', value: null } });
                                            }
                                            handleChangeExtraFilters({
                                                target: {
                                                    name: 'fecha',
                                                    value: value
                                                }
                                            });
                                        }}
                                        disableFuture={true}
                                        format="DD/MM/YYYY"
                                    />
                                </LocalizationProvider>
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Grid>
            {isShowMoreFiltersOpen && (
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} lg={isPerfilBackOffice ? 2.5 : 3}>
                            <Tooltip title={hasVisitDate ? translate('header.extraFilters.onlyOneDateFilterTooltip') : ''}>
                                <span>
                                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                                        <DatePicker
                                            slotProps={{
                                                textField: {
                                                    inputProps: { 'data-testid': TEST_IDS.extraFilters.visits.startDate }
                                                },
                                                field: { clearable: true }
                                            }}
                                            sx={{ display: 'flex' }}
                                            label={translate('header.extraFilters.startDate')}
                                            value={draftFechaInicio || null}
                                            name="fechaInicio"
                                            disabled={hasVisitDate}
                                            onChange={(value: any) => setDraftFechaInicio(value)}
                                            disableFuture={true}
                                            maxDate={draftFechaFin || undefined}
                                            format="DD/MM/YYYY"
                                        />
                                    </LocalizationProvider>
                                </span>
                            </Tooltip>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={isPerfilBackOffice ? 2.5 : 3}>
                            <Tooltip title={hasVisitDate ? translate('header.extraFilters.onlyOneDateFilterTooltip') : ''}>
                                <span>
                                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                                        <DatePicker
                                            slotProps={{
                                                textField: {
                                                    inputProps: { 'data-testid': TEST_IDS.extraFilters.visits.endDate }
                                                },
                                                field: { clearable: true }
                                            }}
                                            sx={{ display: 'flex' }}
                                            label={translate('header.extraFilters.endDate')}
                                            value={draftFechaFin || null}
                                            name="fechaFin"
                                            disabled={hasVisitDate}
                                            onChange={(value: any) => setDraftFechaFin(value)}
                                            disableFuture={true}
                                            minDate={draftFechaInicio || undefined}
                                            format="DD/MM/YYYY"
                                        />
                                    </LocalizationProvider>
                                </span>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
};

export { VisitsFilters };
